import React from 'react';

// ── 메뉴 설정 데이터 (JSON) ──────────────────────────────────
// type
//   'link'     : 단순 경로 이동. path 필드 사용.
//   'url'      : 외부 URL 이동. url / sessionUrl 필드 사용.
//   'category' : 드롭다운 그룹.
//                items 배열이 있으면 하위 항목 표시,
//                items가 없으면(또는 빈 배열) 단순 표시용 레이블만 렌더링.
//                items 항목: { label, path } 또는 { label, action }
//                itemsFrom: ctx 키로부터 동적 항목을 읽을 때 사용
//                        => ui/Sidebar.js > menuItemCtx값의 dataSources에 값이 존재 해야함 신규라면 신규값을 추가
//   'action'   : 클릭 시 함수 직접 호출. action 필드에 핸들러 키 지정.
//                ctx 에서 'on' + 대문자시작(action) 으로 핸들러를 찾음.
//                예) action: 'openFormModal'  →  ctx.onOpenFormModal()
export const MENU_CONFIG = {
  source: 'fallback',
  schema: 'wrapped-menu-config-v1',
  menusByKey: {
    myDesk: {
      type: 'link',
      label: 'My Desk',
      icon: 'fas fa-home',
      path: '/portal',
    },
    mail: {
      type: 'url',
      label: '전자메일',
      icon: 'fas fa-envelope',
      url: 'https://cmail.techni.co.kr',
      sessionUrl: 'https://cmail.techni.co.kr/mailcow_auto_login_enhanced_debug.php',
    },
    approval: {
      type: 'category',
      label: '전자결재',
      icon: 'fas fa-file-signature',
      items: [
        { label: '결재 작성', action: 'openFormModal' },
        { label: '작성중문서', path: '/aprvdoc?status=draft' },
        { label: '진행함', path: '/aprvdoc?status=request' },
        { label: '문서함', path: '/aprvdoc?status=complete' },
        { label: '반려함', path: '/aprvdoc?status=reject' },
      ],
    },
    board: {
      type: 'category',
      label: '게시판',
      icon: 'fas fa-comments',
      itemsFrom: 'filteredBoardCodes',
      itemId: 'id',
      itemLabel: 'name',
      pathTemplate: '/board?code_id={id}',
    },
    reservation: {
      type: 'category',
      label: '자원예약',
      icon: 'fas fa-calendar-check',
      items: [
        { label: '예약등록', path: '/reservations/new' },
        { label: '예약 승인/대기 현황', path: '/reservations/calendar?status=active' },
        { label: '예약 승인 현황', path: '/reservations/calendar?status=approve' },
        { label: '예약 대기 현황', path: '/reservations/calendar?status=pending' },
        { label: '예약 상태 조회', path: '/reservations/list?status=reject' },
      ],
    },
    schedule: {
      type: 'category',
      label: '일정관리',
      icon: 'fas fa-calendar-alt',
      items: [
        { label: '일정작성', path: '/schedule/new' },
        { label: '일정현황', path: '/schedule/calendar' },
        { label: '전사공유일정관리', path: '/schedule/list?status=request' },
      ],
    },
  },
};

export const DEFAULT_MENU_MAP = MENU_CONFIG.menusByKey;

function resolveMenuConfigMap(menuConfig = MENU_CONFIG) {
  if (menuConfig && menuConfig.menusByKey) {
    return menuConfig.menusByKey;
  }
  return menuConfig || {};
}

function isActiveFlag(value) {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'y' || normalized === 'yes';
}

function normalizeMenuType(type) {
  return String(type ?? '').trim().toLowerCase();
}

function isExternalUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function openExternalUrl(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ── 렌더 헬퍼 ────────────────────────────────────────────────

// action 키 → ctx 핸들러 함수 조회
// 예) 'openFormModal' → ctx.onOpenFormModal
function resolveActionHandler(actionKey, ctx) {
  const handlerName = 'on' + actionKey.charAt(0).toUpperCase() + actionKey.slice(1);
  return ctx[handlerName] || null;
}

// DB 컬럼명(snake_case)과 프론트 설정명(camelCase)을 한 곳에서 통일한다.
function normalizeMenuConfig(config) {
  const normalizedType = normalizeMenuType(config?.type);
  const resolvedUrl = normalizedType === 'url'
    ? config.url
    : (config.url ?? config.path ?? config.link_url ?? config.linkUrl);
  return {
    ...config,
    type: normalizedType || 'link',
    url: resolvedUrl,
    sessionUrl: config.sessionUrl ?? config.session_url,
    itemsFrom: config.itemsFrom ?? config.items_from,
    itemId: config.itemId ?? config.item_id_field,
    itemLabel: config.itemLabel ?? config.item_label_field,
    pathTemplate: config.pathTemplate ?? config.path_template,
  };
}

function getCategoryItems(config, ctx) {
  if (!config.itemsFrom) {
    return config.items || [];
  }

  const sourceItems = ctx.dataSources?.[config.itemsFrom] || ctx[config.itemsFrom] || [];
  return sourceItems.map((item) => ({
    id: item[config.itemId],
    label: item[config.itemLabel],
    path: config.pathTemplate.replace(`{${config.itemId}}`, item[config.itemId]),
  }));
}

export function buildMenuConfigFromDb(menuRows, itemRows, fallbackConfig = DEFAULT_MENU_MAP) {
  if (!Array.isArray(menuRows) || menuRows.length === 0) {
    return resolveMenuConfigMap(fallbackConfig);
  }

  const activeMenus = menuRows
    .filter((row) => isActiveFlag(row.is_active))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.menu_id || 0) - (b.menu_id || 0));

  const configMap = {};
  const menuIdToKey = {};

  activeMenus.forEach((row) => {
    const key = row.menu_key;
    if (!key) return;

    const normalized = normalizeMenuConfig(row);
    configMap[key] = {
      ...normalized,
      label: normalized.label || key,
      icon: normalized.icon || 'fas fa-circle',
      items: [],
    };
    menuIdToKey[String(row.menu_id)] = key;
  });

  const activeItems = Array.isArray(itemRows)
    ? itemRows
      .filter((row) => isActiveFlag(row.is_active))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.item_id || 0) - (b.item_id || 0))
    : [];

  activeItems.forEach((item) => {
    const menuKey = menuIdToKey[String(item.menu_id)];
    if (!menuKey || !configMap[menuKey]) return;

    const resolvedUrl = item.url ?? item.path ?? item.link_url ?? item.linkUrl;

    configMap[menuKey].items.push({
      id: item.item_id,
      label: item.label,
      path: item.path,
      url: resolvedUrl,
      action: item.action,
    });
  });

  return configMap;
}

export function getCategoryMenuKeys(menuConfig = MENU_CONFIG) {
  const menuMap = resolveMenuConfigMap(menuConfig);
  return Object.entries(menuMap)
    .filter(([_, config]) => normalizeMenuConfig(config).type === 'category')
    .map(([key]) => key);
}

function renderLinkMenu(menuKey, config, ctx) {
  const { collapsed, handleSimpleNavigate } = ctx;
  return (
    <button key={menuKey}
      className="btn btn-link nav-link w-100 text-start"
      onClick={() => handleSimpleNavigate(config.path)}
    >
      <i className={`${config.icon} me-2`}></i>
      {!collapsed && config.label}
    </button>
  );
}

function renderUrlMenu(menuKey, config, ctx) {
  const { collapsed, sessionValue, setOpenMenu } = ctx;
  const sessionUrl = String(config.sessionUrl || '').trim();
  return (
    <button key={menuKey}
      className="btn btn-link nav-link w-100 text-start"
      onClick={() => {
        if (sessionValue && sessionValue !== 'null' && sessionUrl) {
          window.open(
            `${sessionUrl}?key=${encodeURIComponent(sessionValue)}`,
            '_blank'
          );
        } else {
          openExternalUrl(config.url);
        }
        setOpenMenu(null);
      }}
    >
      <i className={`${config.icon} me-2`}></i>
      {!collapsed && config.label}
    </button>
  );
}

function renderActionMenu(menuKey, config, ctx) {
  const { collapsed } = ctx;
  const handler = resolveActionHandler(config.action, ctx);
  return (
    <button key={menuKey}
      className="btn btn-link nav-link w-100 text-start"
      onClick={() => handler && handler()}
    >
      <i className={`${config.icon} me-2`}></i>
      {!collapsed && config.label}
    </button>
  );
}

function renderCategoryMenu(menuKey, config, ctx) {
  const { collapsed, openMenu, toggleMenu, handleSimpleNavigate, setOpenMenu } = ctx;
  const pos    = ctx[`${menuKey}Pos`];
  const btnRef = ctx[`${menuKey}BtnRef`];

  const items = getCategoryItems(config, ctx);

  const hasItems = items.length > 0;

  return (
    <div key={menuKey} className="nav-item">
      <button
        ref={btnRef}
        className="btn btn-link nav-link w-100 text-start d-flex justify-content-between align-items-center"
        onClick={() => hasItems && toggleMenu(menuKey)}
        style={!hasItems ? { cursor: 'default' } : undefined}
      >
        <span>
          <i className={`${config.icon} me-2`}></i>
          {!collapsed && config.label}
        </span>
        {!collapsed && hasItems && (
          <i className={`fas fa-chevron-${openMenu === menuKey ? 'down' : 'right'}`}></i>
        )}
      </button>
      {hasItems && openMenu === menuKey && (
        <ul
          className={`sidebar-submenu list-unstyled ${!collapsed ? 'ps-4' : ''} ${collapsed ? 'floating' : 'open'}`}
          style={collapsed ? { position: 'fixed', top: pos.top, left: 80 } : undefined}
        >
          {items.map((item, idx) => (
            <li key={item.id ?? idx}>
              <button
                className="dropdown-item"
                onClick={() => {
                  if (item.action) {
                    const handler = resolveActionHandler(item.action, ctx);
                    if (handler) handler();
                    return;
                  }

                  const externalUrl = item.url || item.path;
                  if (isExternalUrl(externalUrl)) {
                    openExternalUrl(externalUrl);
                    setOpenMenu(null);
                    return;
                  }

                  handleSimpleNavigate(item.path, menuKey);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function renderMenuItem(menuKey, ctx, menuConfig = MENU_CONFIG) {
  const menuMap = resolveMenuConfigMap(menuConfig);
  const config = normalizeMenuConfig(menuMap[menuKey]);
  if (!config) return null;
  switch (config.type) {
    case 'link':     return renderLinkMenu(menuKey, config, ctx);
    case 'url':      return renderUrlMenu(menuKey, config, ctx);
    case 'category': return renderCategoryMenu(menuKey, config, ctx);
    case 'action':   return renderActionMenu(menuKey, config, ctx);
    default:         return null;
  }
}

export function renderMenuById(menuId, ctx, menuConfig = MENU_CONFIG) {
  return renderMenuItem(menuId, ctx, menuConfig);
}
